import { useMemo, useState } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import {
  ArrowLeft,
  Package,
  Smartphone,
  Tablet,
  Laptop,
  Headphones,
  ImageIcon,
  Info,
  Check,
  ChevronsUpDown,
} from '@/lib/icons';

import {
  CreateDeviceModelSchema,
  type CreateDeviceModel,
  type DeviceCategory,
} from '@/features/device-models/device-model';
import {
  createDeviceModel,
  getDeviceBrands,
} from '@/features/device-models/api';
import { deviceCatalogKeys, invalidateDeviceCatalog } from '@/features/device-models/keys';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/hooks/useAuth';
import { useAppSelector } from '@/store/hooks';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { ActionButton } from '@/components/ui/action-button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Card, CardContent } from '@/components/ui/card';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  FormDescription,
} from '@/components/ui/form';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { isSuperAdmin } from '@/lib/auth/roles';

export const Route = createFileRoute('/_layout/device-models/new')({
  component: NewDeviceModelPage,
});

// ── Category card config ─────────────────────────────────────────

const CATEGORY_CONFIG: {
  value: DeviceCategory;
  icon: typeof Smartphone;
  gradient: string;
  ring: string;
}[] = [
  {
    value: 'phone',
    icon: Smartphone,
    gradient: 'from-blue-500/15 to-blue-600/5',
    ring: 'ring-blue-500/60',
  },
  {
    value: 'tablet',
    icon: Tablet,
    gradient: 'from-violet-500/15 to-violet-600/5',
    ring: 'ring-violet-500/60',
  },
  {
    value: 'laptop',
    icon: Laptop,
    gradient: 'from-amber-500/15 to-amber-600/5',
    ring: 'ring-amber-500/60',
  },
  {
    value: 'accessory',
    icon: Headphones,
    gradient: 'from-emerald-500/15 to-emerald-600/5',
    ring: 'ring-emerald-500/60',
  },
];

// ── Page ──────────────────────────────────────────────────────────

function NewDeviceModelPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { user } = useAuth();
  const selectedTenantId = useAppSelector(
    (state) => state.tenant.selectedTenantId
  );
  const isCrossTenantOperator =
    isSuperAdmin(user) && !user?.tenantId;
  const [brandPopoverOpen, setBrandPopoverOpen] = useState(false);

  // Fetch the platform's seeded brand list (preserved across resets).
  const { data: brandsData } = useQuery({
    queryKey: deviceCatalogKeys.brands({ isActive: true }),
    queryFn: () => getDeviceBrands({ isActive: true }),
    staleTime: 5 * 60 * 1000,
  });

  const existingBrands = useMemo(() => {
    const brands = brandsData?.value ?? [];
    return brands
      .map((b) => ({ value: b.name, label: b.name }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [brandsData]);

  const form = useForm<CreateDeviceModel>({
    resolver: zodResolver(CreateDeviceModelSchema),
    defaultValues: {
      brand: '',
      name: '',
      description: '',
      category: 'phone',
      imageUrl: '',
      isActive: true,
    },
  });

  const onSubmit = async (values: CreateDeviceModel) => {
    if (isCrossTenantOperator && !selectedTenantId) {
      toast({
        title: t('common.error'),
        description:
          'Select an organization from the sidebar before creating a device model.',
        variant: 'destructive',
      });
      return;
    }
    try {
      const result = await createDeviceModel(values);
      if (result.succeeded && result.value) {
        await invalidateDeviceCatalog(queryClient);
        toast({
          title: t('common.success'),
          description: t('deviceModels.toast.modelCreated'),
          variant: 'success',
        });
        navigate({
          to: '/device-models/$modelId',
          params: { modelId: result.value.id },
        });
      } else {
        toast({
          title: t('common.error'),
          description:
            result.errors?.[0] ?? t('deviceModels.toast.modelCreateFailed'),
          variant: 'destructive',
        });
      }
    } catch (err: unknown) {
      const apiErr = err as { message?: string; statusCode?: number };
      const isTenantError =
        apiErr.message?.toLowerCase().includes('tenant') ?? false;
      toast({
        title: t('common.error'),
        description: isTenantError
          ? 'Please select an organization from the sidebar before creating a device model.'
          : (apiErr.message ?? t('deviceModels.toast.modelCreateFailed')),
        variant: 'destructive',
      });
    }
  };

  const selectedCategory = form.watch('category');
  const selectedBrand = form.watch('brand');

  return (
    <div className="space-y-6 max-w-3xl">
      {/* ── Header ──────────────────────────────────────────────── */}
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate({ to: '/device-models' })}
        >
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div className="flex items-center gap-3">
          <div className="p-2.5 rounded-xl bg-gradient-to-br from-primary/20 to-primary/5 shadow-sm">
            <Package className="h-5 w-5 text-primary" />
          </div>
          <div>
            <h1 className="text-2xl font-semibold">
              {t('deviceModels.newModel')}
            </h1>
            <p className="text-muted-foreground text-sm">
              {t('deviceModels.form.newModelSubtitle', {
                defaultValue: 'Add a new device to your product catalog',
              })}
            </p>
          </div>
        </div>
      </div>

      {/* ── Form ────────────────────────────────────────────────── */}
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
          {/* ── Section 1: Brand & Name ─────────────────────────── */}
          <Card className="border-0 shadow-md overflow-hidden">
            <div className="border-b bg-muted/30 px-6 py-3">
              <h2 className="text-sm font-semibold tracking-wide text-foreground/80 uppercase">
                {t('deviceModels.form.modelInfo')}
              </h2>
            </div>
            <CardContent className="p-6 space-y-5">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                {/* Brand combobox */}
                <FormField
                  control={form.control}
                  name="brand"
                  render={({ field }) => (
                    <FormItem className="flex flex-col">
                      <FormLabel>{t('deviceModels.form.brand')}</FormLabel>
                      <Popover
                        open={brandPopoverOpen}
                        onOpenChange={setBrandPopoverOpen}
                      >
                        <PopoverTrigger asChild>
                          <FormControl>
                            <Button
                              variant="outline"
                              role="combobox"
                              aria-expanded={brandPopoverOpen}
                              className={cn(
                                'w-full justify-between font-normal',
                                !field.value && 'text-muted-foreground'
                              )}
                            >
                              {field.value ||
                                t('deviceModels.form.brandPlaceholder', {
                                  defaultValue: 'Select or type a brand...',
                                })}
                              <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                            </Button>
                          </FormControl>
                        </PopoverTrigger>
                        <PopoverContent
                          className="w-[--radix-popover-trigger-width] p-0"
                          align="start"
                        >
                          <Command>
                            <CommandInput
                              placeholder={t('deviceModels.form.brandSearch', {
                                defaultValue: 'Search brands...',
                              })}
                              onValueChange={(search) => {
                                // Allow typing a custom brand that doesn't exist yet
                                if (search) {
                                  field.onChange(search);
                                }
                              }}
                            />
                            <CommandList>
                              <CommandEmpty>
                                <div className="py-2 text-center">
                                  <p className="text-sm text-muted-foreground">
                                    {t('deviceModels.form.brandNotFound', {
                                      defaultValue: 'No existing brand found.',
                                    })}
                                  </p>
                                  {field.value && (
                                    <p className="text-xs text-primary mt-1">
                                      {t('deviceModels.form.brandUseTyped', {
                                        defaultValue:
                                          'Press Enter or click away to use "{{brand}}"',
                                        brand: field.value,
                                      })}
                                    </p>
                                  )}
                                </div>
                              </CommandEmpty>
                              <CommandGroup>
                                {existingBrands.map((brand) => (
                                  <CommandItem
                                    key={brand.value}
                                    value={brand.label}
                                    onSelect={() => {
                                      field.onChange(brand.value);
                                      setBrandPopoverOpen(false);
                                    }}
                                  >
                                    <Check
                                      className={cn(
                                        'mr-2 h-4 w-4',
                                        selectedBrand === brand.value
                                          ? 'opacity-100'
                                          : 'opacity-0'
                                      )}
                                    />
                                    {brand.label}
                                  </CommandItem>
                                ))}
                              </CommandGroup>
                            </CommandList>
                          </Command>
                        </PopoverContent>
                      </Popover>
                      <FormDescription>
                        {t('deviceModels.form.brandHint', {
                          defaultValue:
                            'Pick from existing brands or type a new one',
                        })}
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* Name */}
                <FormField
                  control={form.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('deviceModels.form.name')}</FormLabel>
                      <FormControl>
                        <Input
                          placeholder={t('deviceModels.form.namePlaceholder', {
                            defaultValue: 'e.g. Galaxy Tab A9',
                          })}
                          {...field}
                        />
                      </FormControl>
                      <FormDescription>
                        {t('deviceModels.form.nameHint', {
                          defaultValue: 'The model name without the brand',
                        })}
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              {/* Description */}
              <FormField
                control={form.control}
                name="description"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('deviceModels.form.description')}</FormLabel>
                    <FormControl>
                      <Textarea
                        placeholder={t(
                          'deviceModels.form.descriptionPlaceholder'
                        )}
                        rows={3}
                        className="resize-none"
                        {...field}
                      />
                    </FormControl>
                    <FormDescription>
                      {t('deviceModels.form.descHint', {
                        defaultValue:
                          'Optional. A short summary for internal reference.',
                      })}
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </CardContent>
          </Card>

          {/* ── Section 2: Category ─────────────────────────────── */}
          <Card className="border-0 shadow-md overflow-hidden">
            <div className="border-b bg-muted/30 px-6 py-3">
              <h2 className="text-sm font-semibold tracking-wide text-foreground/80 uppercase">
                {t('deviceModels.form.category')}
              </h2>
            </div>
            <CardContent className="p-6">
              <FormField
                control={form.control}
                name="category"
                render={({ field }) => (
                  <FormItem>
                    <FormDescription className="mb-4">
                      {t('deviceModels.form.categoryHint', {
                        defaultValue:
                          'Choose the type of device this model represents',
                      })}
                    </FormDescription>
                    <FormControl>
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                        {CATEGORY_CONFIG.map((cat) => {
                          const isSelected = field.value === cat.value;
                          const Icon = cat.icon;
                          return (
                            <button
                              key={cat.value}
                              type="button"
                              onClick={() => field.onChange(cat.value)}
                              className={cn(
                                'group relative flex flex-col items-center gap-2 rounded-xl border-2 p-4 transition-all duration-200',
                                'hover:shadow-md hover:scale-[1.02] cursor-pointer',
                                isSelected
                                  ? `border-primary bg-gradient-to-br ${cat.gradient} shadow-md ring-2 ${cat.ring}`
                                  : 'border-border/60 bg-card hover:border-primary/40'
                              )}
                            >
                              <div
                                className={cn(
                                  'rounded-lg p-2.5 transition-colors',
                                  isSelected
                                    ? 'bg-primary/10'
                                    : 'bg-muted group-hover:bg-primary/5'
                                )}
                              >
                                <Icon
                                  className={cn(
                                    'h-6 w-6 transition-colors',
                                    isSelected
                                      ? 'text-primary'
                                      : 'text-muted-foreground group-hover:text-primary/70'
                                  )}
                                />
                              </div>
                              <span
                                className={cn(
                                  'text-sm font-medium transition-colors',
                                  isSelected
                                    ? 'text-primary'
                                    : 'text-muted-foreground group-hover:text-foreground'
                                )}
                              >
                                {t(`deviceModels.categories.${cat.value}`)}
                              </span>
                              {isSelected && (
                                <div className="absolute -top-1.5 -right-1.5 rounded-full bg-primary p-0.5 shadow-sm">
                                  <Check className="h-3 w-3 text-primary-foreground" />
                                </div>
                              )}
                            </button>
                          );
                        })}
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </CardContent>
          </Card>

          {/* ── Section 3: Settings ─────────────────────────────── */}
          <Card className="border-0 shadow-md overflow-hidden">
            <div className="border-b bg-muted/30 px-6 py-3">
              <h2 className="text-sm font-semibold tracking-wide text-foreground/80 uppercase">
                {t('deviceModels.form.settingsSection', {
                  defaultValue: 'Settings',
                })}
              </h2>
            </div>
            <CardContent className="p-6 space-y-4">
              {/* Active toggle */}
              <FormField
                control={form.control}
                name="isActive"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-center justify-between rounded-xl border bg-card px-4 py-3">
                    <div className="space-y-0.5">
                      <FormLabel className="text-base">
                        {t('deviceModels.form.isActive')}
                      </FormLabel>
                      <FormDescription>
                        {t('deviceModels.form.isActiveDescription')}
                      </FormDescription>
                    </div>
                    <FormControl>
                      <Switch
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />

              {/* Image info hint */}
              <div className="flex items-start gap-3 rounded-xl border border-dashed border-muted-foreground/20 bg-muted/20 p-4">
                <div className="rounded-lg bg-primary/10 p-2 shrink-0">
                  <ImageIcon className="h-4 w-4 text-primary" />
                </div>
                <div>
                  <p className="text-sm font-medium text-foreground/80">
                    {t('images.uploadAfterCreateTitle', {
                      defaultValue: 'Product Images',
                    })}
                  </p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {t('images.uploadAfterCreate', {
                      defaultValue:
                        'You can upload product images after creating the model.',
                    })}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* ── Actions ─────────────────────────────────────────── */}
          <div className="flex items-center gap-3 pt-2">
            <ActionButton
              type="submit"
              loading={form.formState.isSubmitting}
              className="bg-gradient-to-r from-primary to-primary/85 hover:from-primary/90 hover:to-primary/75 shadow-md hover:shadow-lg transition-all duration-200 px-8"
            >
              {t('common.actions.create')}
            </ActionButton>
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate({ to: '/device-models' })}
            >
              {t('common.actions.cancel')}
            </Button>
          </div>
        </form>
      </Form>
    </div>
  );
}
